/**
 * Trust Utility hub — four planned admin surfaces expressed as stub
 * pages that link out to what's already live in the API + wallet.
 */
import PlannedFeaturePage from "@/components/admin/PlannedFeaturePage";
import { KeyRound, Fingerprint, FileCheck2 } from "lucide-react";

// InstitutionsPage moved to /pages/admin/InstitutionsConsole.jsx (Feb 2026).
// This file now only exports the remaining Trust Utility placeholders.

export function ConsentLedgerAdminPage() {
  return (
    <PlannedFeaturePage
      eyebrow="Trust Utility · Consent"
      title="Consent Ledger"
      icon={KeyRound}
      description="Every institution data pull is purpose-bound, time-boxed, revocable, and audited. Admin has a system-wide read-only view here — institutions see only their own consent records via their portal."
      landed={[
        {
          label: "Consumer-side wallet — My Consent",
          href: "/wallet/consent",
          note: "Grant / decline / revoke, with a visible per-institution access log.",
        },
        {
          label: "Institution API — POST /api/consent/requests",
          note: "Bearer-auth. Returns a magic-link claim URL the institution forwards to the consumer.",
        },
        {
          label: "Data-pull endpoint — GET /api/consent/requests/{id}/data",
          note: "Enforces status=granted AND not expired. Every read appends to access_log AND records an AuditLog. Payload hash-chained.",
        },
        {
          label: "20 pytest tests including strict tenant isolation",
          note: "Rival Bank cannot see, GET, or PULL any consent record from Demo Bank's tenant. Regression halt if this ever fails.",
        },
      ]}
      planned={[
        { label: "System-wide admin consent search", note: "Filter by tenant, purpose, status, data category" },
        { label: "Consent volume + grant-rate metrics", note: "Time-to-decide, revocation rate, average grant duration" },
        { label: "Regulator evidence-pack export", note: "Bundled PDF+JSON per consent record with hash-chain proof" },
      ]}
    />
  );
}

export function KycOrchestrationPage() {
  return (
    <PlannedFeaturePage
      eyebrow="Trust Utility · Verification"
      title="KYC Orchestration"
      icon={Fingerprint}
      description="Altara is provider-neutral. Consumers enter identity ONCE into their PersonalDataVault; vendors verify in the background; institutions receive the outcome (never the underlying data) via a granted consent. Add new vendors by registering them — no code changes elsewhere."
      landed={[
        {
          label: "PersonalDataVault (consumer wallet)",
          href: "/wallet/identity",
          note: "Consumer's canonical identity: name, DOB, SA ID/passport/asylum/refugee, contact, address. POPIA erasure endpoint included.",
        },
        {
          label: "KycProvider adapter interface",
          note: "kyc_providers.py — pluggable KycProvider(ABC). Register real vendors (Smile ID, iiDentifii, Refinitiv, ThisIsMe, LexisNexis) in the _PROVIDERS registry.",
        },
        {
          label: "MockKycProvider (running today)",
          note: "Deterministic branches by SA ID suffix: .9999=failed, .0000=in_review, format check, age gate. Enables full E2E demos.",
        },
      ]}
      planned={[
        { label: "Live Smile ID adapter", note: "First real vendor integration — biometric + doc + PEP + sanctions" },
        { label: "iiDentifii + Refinitiv adapters", note: "Drop-in registrations against the same interface" },
        { label: "Vendor selection per-tenant", note: "Each institution picks its preferred vendor via TenantConfiguration" },
        { label: "Verified Credential envelope", note: "Mint W3C VC-DM on successful KYC using the existing Ed25519 signer" },
      ]}
    />
  );
}

export function TrustCertificatesAdminPage() {
  return (
    <PlannedFeaturePage
      eyebrow="Trust Utility · Evidence"
      title="Trust Certificates"
      icon={FileCheck2}
      description="Every document dropped into a Journey is Ed25519-signed and hash-chained. Certificates chain across revisions — a fresh version supersedes the previous with prev_hash linkage. Public verification endpoint enables independent proof."
      landed={[
        {
          label: "Certificate issuance on drop-off",
          note: "POST /api/inbox/public/{token}/deposit issues an ATC-… certificate for each document.",
        },
        {
          label: "Public verify page",
          href: "/verify/ATC-example",
          note: "Consumers can share the verify URL with any recipient; anyone can independently confirm the signature.",
        },
        {
          label: "Public JWKS",
          note: "GET /api/trust/public-key — Ed25519 in JWK form for external verifiers.",
        },
        {
          label: "Revision chain",
          note: "Second upload supersedes the first; latest wins. prev_hash preserves the audit chain.",
        },
      ]}
      planned={[
        { label: "Weekly Merkle-root notarisation", note: "Root published to S3 Object Lock (WORM) for external immutability" },
        { label: "AWS KMS-backed signing", note: "Ed25519 private key migrates from Mongo to KMS; existing certificates remain verifiable" },
        { label: "Admin certificate search", note: "Filter by tenant, consumer, journey, date range" },
      ]}
    />
  );
}
